import { Component, OnInit, OnDestroy, ViewChild, ElementRef, AfterViewChecked, ViewEncapsulation, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { Subject, takeUntil } from 'rxjs';
import { ChatService } from './services/chat.service';
import { ChatMessage, ChatSession, TypingIndicator } from './interfaces/chat.interface';

@Component({
  selector: 'app-chat-bubble',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule
  ],
  templateUrl: './chat-bubble.component.html',
  styleUrls: ['./chat-bubble.component.scss'],
  encapsulation: ViewEncapsulation.None,
  animations: [
    trigger('chatWindow', [
      state('closed', style({
        transform: 'scale(0.8) translateY(20px)',
        opacity: 0,
        visibility: 'hidden'
      })),
      state('open', style({
        transform: 'scale(1) translateY(0)',
        opacity: 1,
        visibility: 'visible'
      })),
      transition('closed => open', [
        animate('400ms cubic-bezier(0.25, 0.8, 0.25, 1)')
      ]),
      transition('open => closed', [
        animate('300ms cubic-bezier(0.25, 0.8, 0.25, 1)')
      ])
    ]),
    trigger('bubblePulse', [
      state('idle', style({ transform: 'scale(1)' })),
      state('pulse', style({ transform: 'scale(1.1)' })),
      transition('idle <=> pulse', animate('200ms ease-in-out'))
    ]),
    trigger('messageSlide', [
      transition(':enter', [
        style({ transform: 'translateY(30px)', opacity: 0 }),
        animate('400ms ease-out', style({ transform: 'translateY(0)', opacity: 1 }))
      ])
    ])
  ]
})
export class ChatBubbleComponent implements OnInit, OnDestroy, AfterViewChecked {
  @ViewChild('messagesContainer', { static: false }) messagesContainer!: ElementRef;
  @ViewChild('messageInput', { static: false }) messageInput!: ElementRef;

  isChatOpen = false;
  isMinimized = false;
  unreadCount = 0;
  bubbleState = 'idle';
  currentMessage = '';

  isDragging = false;
  dragStartX = 0;
  dragStartY = 0;
  bubbleX = 30; 
  bubbleY = 30;
  
  // Datos del chat
  messages: ChatMessage[] = [];
  currentSession: ChatSession | null = null;
  isConnected = false;
  typingIndicator: TypingIndicator | null = null;
  
  private destroy$ = new Subject<void>();
  private shouldScrollToBottom = true;

  constructor(private chatService: ChatService) { }
  @HostListener('document:mousemove', ['$event'])
  onDocumentMouseMove(event: MouseEvent): void {
    this.onMouseMove(event);
  }

  @HostListener('document:mouseup', ['$event'])
  onDocumentMouseUp(): void {
    this.onMouseUp();
  }

// Métodos drag
onMouseDown(event: MouseEvent): void {
  this.isDragging = true;
  this.dragStartX = event.clientX;
  this.dragStartY = event.clientY;
  event.preventDefault();
}

onMouseMove(event: MouseEvent): void {
  if (this.isDragging) {
    const deltaX = event.clientX - this.dragStartX; 
    const deltaY = this.dragStartY - event.clientY;
    
    this.bubbleX = Math.max(30, Math.min(window.innerWidth - 100, this.bubbleX - deltaX)); 
    this.bubbleY = Math.max(30, Math.min(window.innerHeight - 100, this.bubbleY + deltaY)); 
    
    this.dragStartX = event.clientX;
    this.dragStartY = event.clientY;
  }
}

onMouseUp(): void {
  this.isDragging = false;
}

  ngOnInit(): void {
    this.subscribeToChat();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterViewChecked(): void {
    if (this.shouldScrollToBottom) {
      this.scrollToBottom();
    }
  }

  private subscribeToChat(): void {
    // Suscribirse a los mensajes
    this.chatService.messages
      .pipe(takeUntil(this.destroy$))
      .subscribe(messages => {
        this.messages = messages;
        this.shouldScrollToBottom = true;
      });

    // Suscribirse al contador de no leídos
    this.chatService.unreadCount
      .pipe(takeUntil(this.destroy$))
      .subscribe(count => {
        this.unreadCount = count;
        if (count > 0) {
          this.animateBubble();
        }
      });

    // Suscribirse a la sesión actual
    this.chatService.currentSession
      .pipe(takeUntil(this.destroy$))
      .subscribe(session => {
        this.currentSession = session;
      });

    // Suscribirse al estado de conexión
    this.chatService.connectionStatus
      .pipe(takeUntil(this.destroy$))
      .subscribe(status => {
        this.isConnected = status;
      });

    // Suscribirse al indicador de escritura
    this.chatService.typingIndicator
      .pipe(takeUntil(this.destroy$))
      .subscribe(indicator => {
        this.typingIndicator = indicator.isTyping ? indicator : null;
        if (indicator.isTyping) {
          this.shouldScrollToBottom = true;
        }
      });
  }

  toggleChat(): void {
    this.isChatOpen = !this.isChatOpen;
    this.isMinimized = false;
    
    if (this.isChatOpen) {
      this.chatService.markAsRead();
      this.focusMessageInput();
    }
    this.animateBubble();
  }

  closeChat(): void {
    this.isChatOpen = false;
    this.isMinimized = false;
  }

  minimizeChat(): void {
    this.isMinimized = true;
    this.isChatOpen = false;
  }

  onSendMessage(event?: Event): void {
    if (event) {
      event.preventDefault();
    }

    if (!this.currentMessage.trim()) {
      return;
    }

    this.chatService.sendMessage(this.currentMessage);
    this.currentMessage = '';
    this.focusMessageInput();
  }

  onMessageInputKeyup(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.onSendMessage();
    }
  }

  private animateBubble(): void {
    this.bubbleState = 'pulse';
    setTimeout(() => {
      this.bubbleState = 'idle';
    }, 200);
  }

  private scrollToBottom(): void {
    if (this.messagesContainer) {
      try {
        setTimeout(() => {
          const container = this.messagesContainer.nativeElement;
          container.scrollTop = container.scrollHeight;
          this.shouldScrollToBottom = false;
        }, 100);
      } catch (err) {
        console.warn('Error al hacer scroll:', err);
      }
    }
  }

  private focusMessageInput(): void {
    if (this.messageInput && this.isChatOpen && !this.isMinimized) {
      setTimeout(() => {
        this.messageInput.nativeElement.focus();
      }, 400);
    }
  }

  // Métodos de utilidad para el template
  isUserMessage(message: ChatMessage): boolean {
    return message.sender.type === 'user';
  }

  isSystemMessage(message: ChatMessage): boolean {
    return message.sender.type === 'system';
  }

  formatTime(date: Date): string {
    return new Intl.DateTimeFormat('es-ES', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    }).format(new Date(date));
  }

  getMessageStatusIcon(message: ChatMessage): string {
    switch (message.status) {
      case 'sending': return '🕐';
      case 'sent': return '✓';
      case 'delivered': return '✓✓';
      case 'read': return '✓✓';
      case 'error': return '❌';
      default: return '';
    }
  }

  // Método para trackBy en ngFor (optimización de rendimiento)
  trackByMessageId(index: number, message: ChatMessage): string {
    return message.id;
  }

  // Método para manejar error de carga de imagen
  onImageError(event: Event): void {
    const img = event.target as HTMLImageElement;
    img.style.display = 'none';
    // Mostrar SVG de respaldo
    const parent = img.parentElement;
    if (parent) {
      parent.innerHTML = `
        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" style="color: #02b397;">
          <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
        </svg>
      `;
    }
  }

  // Método para reconectar manualmente
  retryConnection(): void {
    this.chatService.retryConnection();
  }

  // Método para detectar y convertir enlaces en texto
  parseLinksInText(text: string): string {
    if (!text) return text;
    
    // Regex para detectar URLs (http, https, www)
    const urlRegex = /(https?:\/\/[^\s]+)|(www\.[^\s]+)/gi;
    
    return text.replace(urlRegex, (match) => {
      let url = match;
      
      // Si la URL no tiene protocolo, añadir https://
      if (match.startsWith('www.')) {
        url = 'https://' + match;
      }
      
      // Crear el enlace HTML con target="_blank" para abrir en nueva pestaña
      return `<a href="${url}" target="_blank" rel="noopener noreferrer">${match}</a>`;
    });
  }
}